/**
 * CallClient — runtime /ws protocol client.
 *
 * Wraps the Configural runtime's /ws gateway. Handles the
 * `configure → ready` handshake, message dispatch, and lifecycle.
 * Audio capture/playback (browser), synthetic-patient logic (test
 * harness), and React state are all consumer concerns — the SDK only
 * marshals frames over the socket.
 *
 * Works in browser and Node:
 *   - Browser: uses global WebSocket, you can send PCM via sendAudio().
 *   - Node: imports `ws` (peer dep) lazily.
 *
 * Usage:
 *   const client = new CallClient({
 *     wsUrl: "ws://localhost:3007/ws",
 *     tenantId, subTenantId, configMode: "draft", callMode: "voice",
 *   });
 *   client.on("ready",      ({ callId }) => …);
 *   client.on("transcript", (e) => …);
 *   client.on("toolCall",   (e) => …);
 *   client.on("close",      (e) => …);
 *   await client.connect();
 *   client.sendAudio(pcmBuffer);   // voice
 *   client.sendText("hello");      // text
 *   client.close();
 */

import { CLIENT_MSG, SERVER_MSG, STATE } from "./message-types.js";
import { resolveWebSocket } from "./ws-resolver.js";
import { buildClientContent } from "./gemini-live.js";

export { STATE } from "./message-types.js";

export class CallClient {
  /**
   * @param {object} opts
   * @param {string} opts.wsUrl          ws://… or /ws (resolved relative)
   * @param {string} opts.tenantId
   * @param {string} opts.subTenantId
   * @param {"draft"|"live"} opts.configMode
   * @param {"voice"|"text"} opts.callMode
   * @param {object} [opts.extraConfigure] extra fields to merge into the
   *   `configure` message — used by the test harness for `direction` +
   *   `outbound_task` (synthetic outbound calls). Merged after the
   *   standard fields, so it can override them if needed.
   * @param {string} [opts.voiceKickoffText] voice mode only: send this
   *   text via a Gemini-Live `client_content` envelope as soon as
   *   `ready` arrives, so the agent speaks first. Skip if you'd rather
   *   send your own kickoff or have the patient speak first.
   * @param {Function} [opts.WebSocketImpl] optional WebSocket class override
   */
  constructor({ wsUrl, tenantId, subTenantId, configMode, callMode, extraConfigure, voiceKickoffText, WebSocketImpl } = {}) {
    if (!wsUrl) throw new Error("CallClient: wsUrl is required");
    if (!tenantId) throw new Error("CallClient: tenantId is required");
    if (!subTenantId) throw new Error("CallClient: subTenantId is required");
    if (configMode !== "draft" && configMode !== "live") {
      throw new Error(`CallClient: configMode must be "draft" or "live" (got "${configMode}")`);
    }
    if (callMode !== "voice" && callMode !== "text") {
      throw new Error(`CallClient: callMode must be "voice" or "text" (got "${callMode}")`);
    }

    this.wsUrl = wsUrl;
    this.tenantId = tenantId;
    this.subTenantId = subTenantId;
    this.configMode = configMode;
    this.callMode = callMode;
    this._extraConfigure = extraConfigure || null;
    this._voiceKickoffText = voiceKickoffText || null;
    this._WebSocketImpl = WebSocketImpl;

    this.state = STATE.IDLE;
    this.callId = null;
    this._ws = null;
    this._listeners = new Map();   // event name → Set<fn>
    this._readyResolvers = null;   // { resolve, reject } captured by connect()
  }

  /**
   * Open the WebSocket, send the configure message, and resolve when
   * the server emits `ready`. Rejects on error or unexpected close
   * before ready.
   */
  async connect() {
    if (this.state !== STATE.IDLE) {
      throw new Error(`CallClient.connect(): already ${this.state}`);
    }
    // [VTRACE SDK-1] SDK_CONNECT_ENTER — CallClient.connect() called from
    // the hook. Resolves WebSocket impl (browser global vs node ws).
    console.log(`[VTRACE SDK-1] SDK_CONNECT_ENTER | ts=${new Date().toISOString()} | wsUrl=${this.wsUrl} callMode=${this.callMode} configMode=${this.configMode}`);
    this.state = STATE.CONNECTING;

    const WS = await resolveWebSocket(this._WebSocketImpl);
    // [VTRACE SDK-2] SDK_WS_NEW — about to construct the actual WebSocket.
    // This is where the TCP/TLS handshake begins (browser → admin :3007 →
    // proxy to runtime :3008).
    console.log(`[VTRACE SDK-2] SDK_WS_NEW | ts=${new Date().toISOString()} | impl=${WS?.name || typeof WS}`);
    this._ws = new WS(this.wsUrl);

    // Browser WebSocket uses .binaryType; Node `ws` defaults to nodebuffer.
    if (typeof this._ws.binaryType !== "undefined") {
      this._ws.binaryType = "arraybuffer";
    }

    return new Promise((resolve, reject) => {
      this._readyResolvers = { resolve, reject };

      this._ws.onopen = () => {
        // [VTRACE SDK-3] SDK_WS_OPEN — handshake complete. Mirrors VTRACE 01
        // on the runtime side. Now sending the configure message → VTRACE 02.
        console.log(`[VTRACE SDK-3] SDK_WS_OPEN | ts=${new Date().toISOString()} | readyState=${this._ws.readyState}`);
        this.state = STATE.CONFIGURING;
        this._sendJson({
          type: CLIENT_MSG.CONFIGURE,
          tenantId: this.tenantId,
          subTenantId: this.subTenantId,
          configMode: this.configMode,
          callMode: this.callMode,
          ...(this._extraConfigure || {}),
        });
        // [VTRACE SDK-4] SDK_CONFIGURE_SENT — handshake message on the wire.
        console.log(`[VTRACE SDK-4] SDK_CONFIGURE_SENT | ts=${new Date().toISOString()} | tenantId=${this.tenantId} subTenantId=${this.subTenantId} configMode=${this.configMode} callMode=${this.callMode}`);
      };

      this._ws.onmessage = (event) => this._handleMessage(event);

      this._ws.onerror = (err) => {
        // [VTRACE SDK-9] SDK_WS_ERROR — socket-level error.
        console.log(`[VTRACE SDK-9] SDK_WS_ERROR | ts=${new Date().toISOString()} | message=${err?.message || "unknown"}`);
        this._emit("error", err?.message ? err : new Error("WebSocket error"));
        if (this._readyResolvers) {
          this._readyResolvers.reject(err);
          this._readyResolvers = null;
        }
      };

      this._ws.onclose = (event) => {
        // [VTRACE SDK-10] SDK_WS_CLOSE — socket closed. Mirrors VTRACE 17.
        console.log(`[VTRACE SDK-10] SDK_WS_CLOSE | ts=${new Date().toISOString()} | code=${event?.code} reason=${event?.reason || "n/a"} state=${this.state}`);
        const closeInfo = { code: event?.code, reason: event?.reason || "" };
        const wasNotReady = this.state !== STATE.READY && this.state !== STATE.CLOSING;
        this.state = STATE.CLOSED;
        if (this._readyResolvers && wasNotReady) {
          this._readyResolvers.reject(
            new Error(`WebSocket closed before ready (code=${closeInfo.code} reason=${closeInfo.reason || "unknown"})`)
          );
          this._readyResolvers = null;
        }
        this._emit("close", closeInfo);
      };
    });
  }

  /**
   * Voice mode: send a PCM audio chunk. Encoded for the Gemini-Live
   * passthrough envelope the runtime currently expects. PCM should be
   * 16-bit, 16 kHz, little-endian.
   *
   * @param {ArrayBuffer | Uint8Array | Buffer} pcm
   */
  sendAudio(pcm) {
    if (this.callMode !== "voice") {
      throw new Error("CallClient.sendAudio(): only valid in voice mode");
    }
    if (this.state !== STATE.READY) return;  // silently drop pre-ready audio
    // [VTRACE SDK-5] SDK_SEND_AUDIO — PCM chunk going on the wire.
    // Sampled (1st + every 100th) — matches runtime VTRACE 08.
    if (typeof globalThis.__vtraceSdkAudioUp === "undefined") globalThis.__vtraceSdkAudioUp = 0;
    globalThis.__vtraceSdkAudioUp++;
    if (globalThis.__vtraceSdkAudioUp === 1 || globalThis.__vtraceSdkAudioUp % 100 === 0) {
      const _bytes = pcm?.byteLength || (pcm?.length ?? 0);
      console.log(`[VTRACE SDK-5] SDK_SEND_AUDIO | ts=${new Date().toISOString()} | n=${globalThis.__vtraceSdkAudioUp} bytes=${_bytes} wsState=${this._ws?.readyState}`);
    }
    this._sendJson({
      realtime_input: {
        media_chunks: [{
          data: _toBase64(pcm),
          mime_type: "audio/pcm;rate=16000",
        }],
      },
    });
  }

  /**
   * Text mode: send a user message. Wire format is Gemini-Live's
   * passthrough envelope (`client_content.turns[].parts[].text`)
   * because the runtime gateway parses that shape on the way to the
   * provider.
   *
   * @param {string} text
   */
  sendText(text) {
    if (this.callMode !== "text") {
      throw new Error("CallClient.sendText(): only valid in text mode");
    }
    if (this.state !== STATE.READY) {
      throw new Error(`CallClient.sendText(): not ready (state=${this.state})`);
    }
    this._sendJson(buildClientContent(text));
  }

  /**
   * Escape hatch — send an arbitrary JSON object as the next message.
   *
   * Voice mode requires this for the Gemini-Live `client_content` kickoff
   * envelope and any future provider-specific control messages the
   * runtime forwards verbatim. Most consumers should not need this.
   *
   * @param {object} obj
   */
  sendRaw(obj) {
    if (this.state !== STATE.READY) {
      throw new Error(`CallClient.sendRaw(): not ready (state=${this.state})`);
    }
    this._sendJson(obj);
  }

  /**
   * Close the connection. Emits `close` once the WS actually closes.
   */
  close(reason) {
    if (this.state === STATE.CLOSED || this.state === STATE.CLOSING) return;
    this.state = STATE.CLOSING;
    try {
      this._ws?.close(1000, reason || "client_close");
    } catch { /* no-op */ }
  }

  // ─── Event subscription ──────────────────────────────────────

  /**
   * Subscribe to an event. Supported events:
   *   "ready"        — { callId, mode }
   *   "transcript"   — { role, text, agent, time }
   *   "audioChunk"   — ArrayBuffer (voice mode, agent audio)
   *   "toolCall"     — { name, args, result, time?, duration_ms? }
   *   "agentSwitch"  — { agent, task? }
   *   "interrupted"  — {} (voice mode)
   *   "processing"   — {} (text mode)
   *   "turnComplete" — { … }
   *   "rateChange"   — { rate, reason? } (Twilio bridge — adaptive playback speed)
   *   "raw"          — the parsed JSON message OR ArrayBuffer (escape hatch
   *                    for messages the SDK doesn't surface as named events)
   *   "error"        — Error
   *   "close"        — { code, reason }
   *
   * @param {string} event
   * @param {Function} handler
   * @returns {() => void} unsubscribe
   */
  on(event, handler) {
    if (typeof handler !== "function") throw new TypeError("handler must be a function");
    let set = this._listeners.get(event);
    if (!set) { set = new Set(); this._listeners.set(event, set); }
    set.add(handler);
    return () => set.delete(handler);
  }

  off(event, handler) {
    this._listeners.get(event)?.delete(handler);
  }

  // ─── Internals ───────────────────────────────────────────────

  _emit(event, payload) {
    const set = this._listeners.get(event);
    if (!set || set.size === 0) return;
    for (const fn of set) {
      try { fn(payload); }
      catch (err) { console.error(`[CallClient] listener for "${event}" threw:`, err); }
    }
  }

  _sendJson(obj) {
    if (this._ws?.readyState === 1 /* OPEN */) {
      this._ws.send(JSON.stringify(obj));
    }
  }

  _handleMessage(event) {
    const data = event?.data;

    // Binary frame → voice agent audio. Pass through as ArrayBuffer.
    if (data instanceof ArrayBuffer) {
      this._emit("audioChunk", data);
      this._emit("raw", data);
      return;
    }
    // Node `ws` emits Buffer for binary by default.
    if (typeof Buffer !== "undefined" && data instanceof Buffer) {
      // Convert to ArrayBuffer view for parity with browser.
      const buf = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
      this._emit("audioChunk", buf);
      this._emit("raw", buf);
      return;
    }

    // Text frame — parse JSON.
    let msg;
    try {
      msg = typeof data === "string" ? JSON.parse(data) : null;
    } catch (err) {
      this._emit("error", new Error(`CallClient: malformed JSON from server: ${err.message}`));
      return;
    }
    if (!msg || typeof msg !== "object") return;

    // [VTRACE SDK-6] SDK_WS_MSG_IN — frame arrived from runtime. Audio
    // (server_content envelopes from Gemini) and tool_result both land here.
    // Server_content frames are large + frequent — log type only.
    const _hasServerContent = !!(msg.serverContent || msg.server_content);
    if (!_hasServerContent) {
      console.log(`[VTRACE SDK-6] SDK_WS_MSG_IN | ts=${new Date().toISOString()} | type=${msg.type || "(passthrough)"}`);
    }

    this._emit("raw", msg);

    switch (msg.type) {
      case SERVER_MSG.READY:
        // [VTRACE SDK-7] SDK_READY — server's "ready" with callId.
        // About to emit "ready" event back to the hook (VTRACE BROWSER-V5).
        console.log(`[VTRACE SDK-7] SDK_READY | ts=${new Date().toISOString()} | callId=${msg.callId} mode=${msg.mode}`);
        this.callId = msg.callId || null;
        this.state = STATE.READY;
        // Voice kickoff: send a client_content envelope so the agent
        // speaks first. Done before emitting "ready" so consumer
        // handlers see a state where the kickoff has already shipped.
        if (this.callMode === "voice" && this._voiceKickoffText) {
          this._sendJson(buildClientContent(this._voiceKickoffText));
        }
        this._emit("ready", { callId: this.callId, mode: msg.mode });
        if (this._readyResolvers) {
          this._readyResolvers.resolve({ callId: this.callId, mode: msg.mode });
          this._readyResolvers = null;
        }
        return;

      case SERVER_MSG.ERROR:
        this._emit("error", new Error(msg.message || "server_error"));
        return;

      case SERVER_MSG.INTERRUPTED:
        this._emit("interrupted", {});
        return;

      case SERVER_MSG.TOOL_RESULT:
        this._emit("toolCall", {
          name: msg.name, args: msg.args, result: msg.result,
          time: msg.time, duration_ms: msg.duration_ms,
        });
        return;

      case SERVER_MSG.SESSION_RESTARTED:
        this._emit("agentSwitch", { agent: msg.agent, task: msg.task });
        return;

      case SERVER_MSG.TEXT_RESPONSE:
        this._emit("transcript", {
          role: "agent", text: msg.text, agent: msg.agent, time: msg.time,
          partial: true,
        });
        return;

      case SERVER_MSG.TURN_COMPLETE:
        this._emit("turnComplete", msg);
        return;

      case SERVER_MSG.PROCESSING:
        this._emit("processing", {});
        return;

      case SERVER_MSG.CLOSED:
        // Server-initiated close. The actual WS onclose will follow.
        this.state = STATE.CLOSING;
        return;

      case SERVER_MSG.RATE_CHANGE:
        this._emit("rateChange", { rate: msg.rate, reason: msg.reason });
        return;

      default:
        // Unknown type — leave for "raw" listeners.
        return;
    }
  }
}

// ─── Helpers ───────────────────────────────────────────────────

function _toBase64(buf) {
  // ArrayBuffer / Uint8Array / Node Buffer → base64 string.
  if (typeof Buffer !== "undefined") {
    if (buf instanceof Buffer) return buf.toString("base64");
    if (buf instanceof Uint8Array) return Buffer.from(buf.buffer, buf.byteOffset, buf.byteLength).toString("base64");
    if (buf instanceof ArrayBuffer) return Buffer.from(buf).toString("base64");
  }
  // Browser path.
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}
